import { TableCell, TableRow } from '@material-ui/core'
import React, { EventHandler, KeyboardEvent, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { ReactComponent as DropdownSVG } from '@percent/admin-dashboard/common/assets/images/dropdown.svg'
import { ErrorView, SearchAndDateFilter, Table, TimeLeft } from '@percent/admin-dashboard/common/components'
import { CountryCodeFlag } from '@percent/admin-dashboard/common/components/countryCodeFlag/CountryCodeFlag'
import { TableColumn, TableSort } from '@percent/admin-dashboard/common/components/table/Table.types'
import { getDate } from '@percent/admin-dashboard/common/utility/getDate'
import { handleClickAsLink } from '@percent/admin-dashboard/common/utility/handleClickAsLink'
import styles from './ComplianceTable.module.scss'
import { ComplianceStatusFilter, ComplianceTableProps } from './ComplianceTable.types'

export function ComplianceTable({
  totalResults,
  subscriptions,
  isLoading,
  previousPage,
  nextPage,
  queryParams,
  setQueryParams,
  errorMessage
}: ComplianceTableProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const tabs = [t('table.pending'), t('table.live'), t('table.invalid')]

  const handleRequestSort = () =>
    setQueryParams({ direction: queryParams.direction === 'ASC' ? 'DESC' : 'ASC', sort: 'name' })

  const handleTabChange = (event: React.ChangeEvent<{}>, newValue: number) => {
    const status = ComplianceStatusFilter[newValue]

    if (status === 'pending') {
      setQueryParams({ status, sort: 'id', direction: 'ASC' })
    } else {
      setQueryParams({ status, sort: undefined, direction: undefined })
    }
  }

  const handleKeyPress: EventHandler<KeyboardEvent<HTMLInputElement>> = useCallback(
    event => {
      if (event.key === 'Enter') {
        setQueryParams({
          query: (event.target as HTMLInputElement).value
        })
      }
    },
    [setQueryParams]
  )

  const filters = useMemo(
    () => (
      <SearchAndDateFilter
        handleKeyPress={handleKeyPress}
        maxLength={255}
        searchPhrase={queryParams.query ?? ''}
        placeholder={t('form.monitoringSearchPlaceholder')}
        setQueryParams={setQueryParams}
      />
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [handleKeyPress, queryParams.query, t, setQueryParams]
  )

  const columns = useMemo(() => {
    const commonColumns: Array<TableColumn> = [
      { id: 'country', isSortable: false },
      { id: 'organization', isSortable: true },
      { id: 'partner', isSortable: false }
    ]

    if (queryParams.status === 'pending') {
      commonColumns.push(
        { id: 'requestDate', isSortable: false, props: { width: '100px' } },
        { id: 'timeLeft', isSortable: false }
      )
    }

    return commonColumns
  }, [queryParams.status])

  if (errorMessage) {
    return <ErrorView errorMessage={errorMessage} />
  }

  return (
    <Table
      isLoading={isLoading}
      totalResults={totalResults}
      data={subscriptions}
      previousPage={previousPage}
      nextPage={nextPage}
      value={
        ComplianceStatusFilter[queryParams.status as keyof typeof ComplianceStatusFilter] ??
        ComplianceStatusFilter.pending
      }
      handleChange={handleTabChange}
      handleRequestSort={handleRequestSort}
      order={(queryParams.direction?.toLowerCase() as TableSort) ?? 'asc'}
      orderBy="nonprofit"
      columns={columns}
      tabs={tabs}
      emptyTableText={t('table.emptyMonitoring')}
      filtersContent={filters}
    >
      {subscriptions?.map(row => (
        <TableRow
          key={row.id}
          onClick={handleClickAsLink(`/organizations/${row.organisationId}/compliance`, navigate)}
          className={row.organisationId && styles.row}
        >
          <TableCell>
            <CountryCodeFlag countryCode={row.countryCode} />
          </TableCell>
          <TableCell>{row.organisationName}</TableCell>
          <TableCell className={styles.date}>{row.partnerName}</TableCell>
          {queryParams.status === 'pending' && (
            <TableCell align="right" className={styles.date} style={{ padding: '16px 50px 16px 0', textAlign: 'left' }}>
              {getDate(row.createdAt)}
            </TableCell>
          )}
          {queryParams.status === 'pending' && (
            <TableCell className={styles.date}>
              <TimeLeft requestedDate={row.createdAt} />
            </TableCell>
          )}
          <TableCell align="right" className={styles.rowIconForward} style={{ padding: '0' }}>
            <DropdownSVG className={styles.arrow} />
          </TableCell>
        </TableRow>
      ))}
    </Table>
  )
}
